import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Search, Trash2 } from "lucide-react";
import { supabase } from "../../../lib/supabase";
import { deleteFromFtp } from "../../../lib/deleteFromFtp";

interface Product {
  id: string;
  name: string;
  description: string;
  price: string;
  imageUrl: string;
  category: string;
}

const ProductList: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const fetchProducts = async () => {
    const { data, error } = await supabase.from('products').select('*');
    if (error) {
        console.error(error);
    } else {
        setProducts(data as Product[]);
        setFilteredProducts(data as Product[]);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    const results = products.filter((item) =>
      item.name.toLowerCase().includes(search.toLowerCase()) || 
      item.category.toLowerCase().includes(search.toLowerCase())
    );
    setFilteredProducts(results);
  }, [search, products]);

  const handleDelete = async (id: string) => {
      if(!window.confirm("Bu ürünü silmek istediğinize emin misiniz?")) return;
      
      const itemToDelete = products.find(p => p.id === id);

      try {
          const { error } = await supabase.from('products').delete().eq('id', id);
          if (!error) {
              if (itemToDelete?.imageUrl) deleteFromFtp(itemToDelete.imageUrl);
              fetchProducts();
          } else {
              console.error(error);
          }
      } catch (error) {
          console.error(error);
      }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Ürün Yönetimi</h1>
          <p className="text-gray-500">Aksesuar ve ürünleri yönetin</p>
        </div>
        <Link
          to="/panel/products/add"
          className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg flex items-center shadow-sm transition-all"
        >
          <Plus className="w-5 h-5 mr-2" />
          Yeni Ürün Ekle
        </Link>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-4 border-b border-gray-100">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Ürün veya kategori ara..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {loading ? (
          <div className="p-8 text-center text-gray-500">Yükleniyor...</div>
        ) : filteredProducts.length === 0 ? (
          <div className="p-8 text-center text-gray-500">Ürün bulunamadı.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead className="bg-gray-50 text-gray-600 font-medium text-sm">
                <tr>
                  <th className="p-4">Görsel</th>
                  <th className="p-4">Ürün Adı</th>
                  <th className="p-4">Kategori</th>
                  <th className="p-4">Fiyat</th>
                  <th className="p-4">İşlem</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredProducts.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                    <td className="p-4">
                      <div className="w-12 h-12 rounded bg-gray-200 overflow-hidden">
                         <img 
                           src={item.imageUrl} 
                           alt={item.name} 
                           className="w-full h-full object-cover"
                           onError={(e) => {(e.target as HTMLImageElement).src = '/logo.svg'}} 
                         />
                      </div>
                    </td>
                    <td className="p-4 font-medium text-gray-800">{item.name}</td>
                    <td className="p-4 text-gray-600">
                        <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">{item.category}</span>
                    </td>
                    <td className="p-4 text-green-600 font-semibold">{item.price}</td>
                    <td className="p-4">
                        <button onClick={() => handleDelete(item.id)} className="text-red-500 hover:text-red-700">
                            <Trash2 size={20} />
                        </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductList;
